import { createMatch, resolveSettings, type MatchRecord, validateOptions, type AnyGameDefinition, type OptionValue, type GameRegistry, type Match, type Seat, type Viewer } from '@campfire/engine';
import {
  CHAT_HISTORY,
  PROTOCOL_VERSION,
  type ChatMessage,
  parseClientMessage,
  type ClientMessage,
  type ErrorCode,
  type HostMessage,
  type LobbyMessage,
  type MemberInfo,
  type Role,
} from './protocol.ts';

/** Anything that can carry a string to one peer (WebSocket, BLE channel, in-memory). */
export interface PeerLink {
  send(data: string): void;
  /** Hang up (e.g. a removed player). Optional: in-memory links just stop receiving. */
  close?(): void;
}

/**
 * Everything needed to bring a table back after the host restarts (FR-NET-17): the same code,
 * members (who reconnect with their tokens), settings, and the game in progress as its record,
 * which is replayed deterministically.
 */
export interface HostSnapshot {
  readonly v: 1;
  readonly roomCode: string;
  readonly roomName: string | null;
  readonly members: readonly { readonly id: string; readonly token: string; readonly name: string; readonly role: Role; readonly account?: string; readonly pushToken?: string }[];
  readonly settings: Readonly<Record<string, Readonly<Record<string, OptionValue>>>>;
  readonly banned: readonly string[];
  readonly departed: Readonly<Record<string, string>>;
  readonly chat?: readonly ChatMessage[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly running: { readonly seats: readonly string[]; readonly record: MatchRecord<any, any> } | null;
}

/** Returned to the transport for each connection; transports feed it inbound data and close events. */
export interface PeerHandle {
  receive(data: string): void;
  disconnect(): void;
}

export interface HostOptions {
  readonly registry: GameRegistry;
  /** Unguessable id generator (crypto.randomUUID in production). Injected to keep this package platform-free. */
  readonly newId: () => string;
  /** Seed generator for each new match. */
  readonly newSeed: () => string;
  readonly roomCode: string;
  readonly maxMembers?: number;
  /** Initial table name; otherwise "<owner>'s table". */
  readonly roomName?: string;
  /** Resume a saved table; its room code wins over `roomCode`. */
  readonly restore?: HostSnapshot;
  /** Wall clock for chat timestamps (injected: this package stays platform-free). */
  readonly now?: () => number;
  /**
   * A seated player with a push token became due while away or disconnected (FR-PUSH-02). The
   * transport sends the notification; tokens never reach other players.
   */
  readonly notifyTurn?: (n: TurnNotice) => void;
}

export interface TurnNotice {
  readonly pushToken: string;
  readonly roomCode: string;
  readonly roomName: string;
  readonly game: string;
  readonly player: string;
}

interface Member {
  readonly id: string;
  readonly token: string;
  readonly name: string;
  readonly role: Role;
  /** Opaque id of the verified sign-in that owns this seat (ADR-0017); never the raw provider id. */
  account?: string;
  link: PeerLink | null;
  away?: boolean;
  lastSeen?: number;
  /** APNs device token from the iOS app (FR-PUSH-01). */
  pushToken?: string;
}

interface Running {
  readonly game: AnyGameDefinition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly match: Match<any, any, any, any>;
  /** seat index → member id */
  readonly seats: readonly string[];
}

export interface HostSession {
  readonly roomCode: string;
  /** `identity.account` is set by a transport that verified a sign-in (ADR-0017). */
  connect(link: PeerLink, identity?: { readonly account?: string }): PeerHandle;
  /** Introspection for tests and host UIs. */
  members(): readonly MemberInfo[];
  readonly match: Running['match'] | null;
  /** Called with every lobby change, e.g. to refresh a local-network announcement. */
  onLobby(cb: (lobby: LobbyMessage) => void): void;
  /** Called after any change worth saving (lobby or game). */
  onChange(cb: () => void): void;
  snapshot(): HostSnapshot;
}

/**
 * Authoritative session (ADR-0003, ADR-0009): lobby, seating, match lifecycle,
 * and per-viewer redacted broadcast. Transport-agnostic.
 */
export function createHostSession(opts: HostOptions): HostSession {
  const { registry, newId, newSeed } = opts;
  const roomCode = opts.restore?.roomCode ?? opts.roomCode;
  const maxMembers = opts.maxMembers ?? 32;
  const members = new Map<string, Member>(); // by id, insertion = join order
  let running: Running | null = null;
  /** Owner-chosen settings per game id; unset keys fall back to game defaults. */
  const settings = new Map<string, Readonly<Record<string, OptionValue>>>();
  let chosenName: string | null = opts.restore ? opts.restore.roomName : (opts.roomName ?? null);
  const lobbyWatchers: ((l: LobbyMessage) => void)[] = [];
  const changeWatchers: (() => void)[] = [];
  /** Tokens of removed members: they cannot reconnect to their seat. */
  const banned = new Set<string>(opts.restore?.banned ?? []);
  /** Names of removed members still seated in the running game. */
  const departed = new Map<string, string>(Object.entries(opts.restore?.departed ?? {}));
  let chat: ChatMessage[] = [...(opts.restore?.chat ?? [])];
  const now = opts.now ?? (() => 0);

  if (opts.restore) {
    for (const m of opts.restore.members) members.set(m.id, { ...m, link: null });
    for (const [id, v] of Object.entries(opts.restore.settings)) settings.set(id, v);
    const r = opts.restore.running;
    const game = r ? registry.get(r.record.gameId) : undefined;
    if (r && game) {
      // Re-submit the players' moves; engine-driven moves replay themselves from the same seed.
      const match = createMatch(game, { seats: r.record.seats, seed: r.record.seed, options: r.record.options });
      for (const e of r.record.log) if (e.actor !== 'system') match.submit({ seat: e.actor, action: e.action, seq: match.seq });
      running = { game, match, seats: r.seats };
    }
  }

  const send = (m: Member, msg: HostMessage) => m.link?.send(JSON.stringify(msg));
  const seatOf = (id: string): Seat | null => {
    if (!running) return null;
    const s = running.seats.indexOf(id);
    return s === -1 ? null : s;
  };
  const ownerId = (): string | null => {
    for (const m of members.values()) if (m.role === 'player' && m.link) return m.id;
    return null;
  };
  const info = (m: Member): MemberInfo => ({
    id: m.id,
    name: m.name,
    role: m.role,
    connected: m.link !== null,
    seat: seatOf(m.id),
    away: m.link !== null && !!m.away,
    verified: !!m.account,
    lastSeen: m.lastSeen ?? 0,
  });

  const lobby = (): LobbyMessage => ({
    t: 'lobby',
    roomCode,
    roomName: chosenName ?? (() => {
      const owner = members.get(ownerId() ?? '');
      return owner ? `${owner.name}’s table` : 'Campfire table';
    })(),
    ownerId: ownerId(),
    members: [...members.values()].map(info),
    games: registry.list().map((g) => ({ id: g.id, name: g.name, minSeats: g.minSeats, maxSeats: g.maxSeats, optionSpecs: g.optionSpecs ?? [], rules: g.rules ?? [] })),
    settings: Object.fromEntries(registry.list().map((g) => [g.id, resolveSettings(g, settings.get(g.id))])),
    gameId: running?.game.id ?? null,
  });

  const stateFor = (m: Member): HostMessage | null => {
    if (!running) return null;
    const seat = seatOf(m.id);
    const viewer: Viewer = seat === null ? { kind: 'spectator' } : { kind: 'seat', seat };
    const { match } = running;
    return {
      t: 'state',
      gameId: running.game.id,
      seq: match.seq,
      seat,
      seatNames: running.seats.map((id) => members.get(id)?.name ?? departed.get(id) ?? '?'),
      activeSeats: match.activeSeats,
      waitingOn: match.waitingOn,
      outcome: match.outcome,
      view: match.view(viewer),
    };
  };

  const broadcastLobby = () => {
    const msg = lobby();
    for (const m of members.values()) send(m, msg);
    for (const cb of lobbyWatchers) cb(msg);
    for (const cb of changeWatchers) cb();
  };
  // Seats that were due after the last state change: only a seat newly due is notified.
  let due = new Set<Seat>();
  const notifyNewlyDue = () => {
    const nowDue = new Set<Seat>(running && !running.match.outcome ? running.match.waitingOn : []);
    if (running && opts.notifyTurn) {
      for (const seat of nowDue) {
        const m = members.get(running.seats[seat] ?? '');
        if (due.has(seat) || !m?.pushToken || (m.link && !m.away)) continue;
        opts.notifyTurn({ pushToken: m.pushToken, roomCode, roomName: lobby().roomName, game: running.game.name, player: m.name });
      }
    }
    due = nowDue;
  };
  const broadcastState = () => {
    for (const m of members.values()) {
      const s = stateFor(m);
      if (s) send(m, s);
    }
    notifyNewlyDue();
    for (const cb of changeWatchers) cb();
  };
  const fail = (link: PeerLink, code: ErrorCode, message: string) =>
    link.send(JSON.stringify({ t: 'error', code, message } satisfies HostMessage));

  function handle(conn: { member: Member | null; link: PeerLink; account: string | undefined }, msg: ClientMessage): void {
    if (msg.t === 'hello') {
      if (conn.member) return fail(conn.link, 'already-joined', 'Already joined');
      if (msg.v !== PROTOCOL_VERSION) return fail(conn.link, 'version', `Host speaks v${PROTOCOL_VERSION}`);
      if (msg.token && banned.has(msg.token)) return fail(conn.link, 'kicked', 'The host removed you from this table');
      // Same device: its seat key. Another device: a signed-in user reclaims the disconnected seat their
      // account owns, whatever the name (ADR-0017); otherwise the one disconnected player with the same
      // name and role takes it back (FR-NET-22) - but never a seat that belongs to an account.
      // Removed players never match.
      const byName = (n: string) => n.trim().toLowerCase();
      const mine = conn.account ? [...members.values()].filter((m) => !m.link && m.role === msg.role && m.account === conn.account) : [];
      const orphans = [...members.values()].filter((m) => !m.link && !m.account && m.role === msg.role && byName(m.name) === byName(msg.name));
      const existing =
        (msg.token ? [...members.values()].find((m) => m.token === msg.token) : undefined) ??
        (mine.length ? mine[0] : orphans.length === 1 ? orphans[0] : undefined);
      let member: Member;
      if (existing) {
        // Reconnect (FR-NET-03): rebind link; a stale socket for the same member is dropped.
        existing.link = conn.link;
        if (conn.account) existing.account = conn.account;
        member = existing;
      } else {
        if (members.size >= maxMembers) return fail(conn.link, 'bad-message', 'Room is full');
        member = { id: newId(), token: newId(), name: msg.name, role: msg.role, link: conn.link, ...(conn.account ? { account: conn.account } : {}) };
        members.set(member.id, member);
      }
      conn.member = member;
      member.away = false;
      member.lastSeen = now();
      send(member, { t: 'welcome', id: member.id, token: member.token, role: member.role });
      broadcastLobby();
      const s = stateFor(member);
      if (s) send(member, s);
      send(member, { t: 'chat', messages: chat, history: true });
      return;
    }

    const member = conn.member;
    if (!member) return fail(conn.link, 'not-joined', 'Send hello first');
    const wasAway = member.away;
    member.lastSeen = now();

    if (msg.t === 'push') {
      member.pushToken = msg.token;
      for (const cb of changeWatchers) cb();
      return;
    }

    if (msg.t === 'presence') {
      member.away = msg.away;
      if (wasAway !== msg.away) broadcastLobby();
      return;
    }
    // Any action means they're back.
    if (wasAway) {
      member.away = false;
      broadcastLobby();
    }

    if (msg.t === 'start') {
      if (member.id !== ownerId()) return fail(conn.link, 'not-owner', 'Only the room owner can start');
      const game = registry.get(msg.gameId);
      if (!game) return fail(conn.link, 'unknown-game', `No game ${msg.gameId}`);
      const seats = [...members.values()].filter((m) => m.role === 'player' && m.link).map((m) => m.id);
      if (seats.length < game.minSeats || seats.length > game.maxSeats) {
        return fail(conn.link, 'seat-count', `${game.name} needs ${game.minSeats}-${game.maxSeats} players; ${seats.length} connected`);
      }
      // Room settings apply to every start and rematch; options sent with start override them once.
      if (msg.options) {
        const check = validateOptions(game, msg.options);
        if (!check.ok) return fail(conn.link, 'bad-options', check.reason);
      }
      try {
        const config = { seats: seats.length, seed: newSeed(), options: { ...settings.get(game.id), ...msg.options } };
        running = { game, match: createMatch(game, config), seats };
      } catch (err) {
        return fail(conn.link, 'bad-options', (err as Error).message);
      }
      broadcastLobby();
      broadcastState();
      return;
    }

    if (msg.t === 'configure') {
      if (member.id !== ownerId()) return fail(conn.link, 'not-owner', 'Only the room owner can change settings');
      const game = registry.get(msg.gameId);
      if (!game) return fail(conn.link, 'unknown-game', `No game ${msg.gameId}`);
      const check = validateOptions(game, msg.options);
      if (!check.ok) return fail(conn.link, 'bad-options', check.reason);
      settings.set(game.id, { ...settings.get(game.id), ...(msg.options as Record<string, OptionValue>) });
      broadcastLobby();
      return;
    }

    if (msg.t === 'rename') {
      if (member.id !== ownerId()) return fail(conn.link, 'not-owner', 'Only the room owner can name the table');
      chosenName = msg.name;
      broadcastLobby();
      return;
    }

    if (msg.t === 'chat') {
      const line: ChatMessage = { id: (chat.at(-1)?.id ?? 0) + 1, from: member.id, name: member.name, text: msg.text, at: now() };
      chat = [...chat, line].slice(-CHAT_HISTORY);
      for (const m of members.values()) send(m, { t: 'chat', messages: [line] });
      for (const cb of changeWatchers) cb();
      return;
    }

    if (msg.t === 'kick') {
      if (member.id !== ownerId()) return fail(conn.link, 'not-owner', 'Only the room owner can remove players');
      const target = members.get(msg.memberId);
      if (!target) return fail(conn.link, 'bad-message', 'No such member');
      if (target.id === member.id) return fail(conn.link, 'bad-message', 'You cannot remove yourself');
      banned.add(target.token);
      if (seatOf(target.id) !== null) departed.set(target.id, target.name);
      members.delete(target.id);
      if (target.link) {
        target.link.send(JSON.stringify({ t: 'error', code: 'kicked', message: 'The host removed you from this table' } satisfies HostMessage));
        target.link.close?.();
        target.link = null;
      }
      broadcastLobby();
      return;
    }

    if (msg.t === 'end') {
      if (member.id !== ownerId()) return fail(conn.link, 'not-owner', 'Only the room owner can end the game');
      if (!running) return fail(conn.link, 'no-match', 'No game in progress');
      running = null;
      broadcastLobby();
      return;
    }

    // act
    if (!running) return fail(conn.link, 'no-match', 'No game in progress');
    const seat = seatOf(member.id);
    if (seat === null) return fail(conn.link, 'not-seated', 'Spectators cannot act');
    const res = running.match.submit({ seat, action: msg.action, seq: msg.seq });
    if (!res.ok) {
      send(member, res.detail === undefined ? { t: 'rejected', reason: res.reason } : { t: 'rejected', reason: res.reason, detail: res.detail });
      return;
    }
    broadcastState();
  }

  return {
    roomCode,
    get match() {
      return running?.match ?? null;
    },
    members: () => [...members.values()].map(info),
    onLobby: (cb) => void lobbyWatchers.push(cb),
    onChange: (cb) => void changeWatchers.push(cb),
    snapshot: () => ({
      v: 1,
      roomCode,
      roomName: chosenName,
      members: [...members.values()].map(({ id, token, name, role, account, pushToken }) => ({ id, token, name, role, ...(account ? { account } : {}), ...(pushToken ? { pushToken } : {}) })),
      settings: Object.fromEntries(settings),
      banned: [...banned],
      departed: Object.fromEntries(departed),
      chat,
      running: running ? { seats: running.seats, record: running.match.record() } : null,
    }),
    connect(link, identity) {
      const conn: { member: Member | null; link: PeerLink; account: string | undefined } = { member: null, link, account: identity?.account };
      return {
        receive(data) {
          const parsed = parseClientMessage(data);
          if (parsed === 'bad-json') return fail(link, 'bad-json', 'Malformed JSON');
          if (parsed === null) return fail(link, 'bad-message', 'Unrecognized message');
          handle(conn, parsed);
        },
        disconnect() {
          const m = conn.member;
          if (m && m.link === link) {
            m.link = null;
            m.lastSeen = now();
            broadcastLobby();
          }
          conn.member = null;
        },
      };
    },
  };
}
